import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { formatCurrency, formatDate, formatDateTime } from '@apply/ui';
import { LoanStatusOptions, LoanType, LostLoanTypeOptions, NoticeTypeOptions, NoticeStatusType } from '@apply/constants';
import Accordion from 'devextreme-react/accordion';
import { LoanStatusType } from '../common/LoanConstants';
import { formatDateMonthYear, getMonths, handleAdminError } from '../../common/SharedFunctions';
import { getLoanTypeName } from '../common/LoanFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';

const LoanSummaryModal = () => {
    const { isOpen, loanId } = useSelector((state) => state.loan.loanSummaryModal);
    const { dispatch } = store;

    const [loan, set_loan] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const loanApi = new LoanApi.LoanApi(applicationInsights);

    const styles = {
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { padding: '10px' },
        formRow: { marginBottom: '0' },
        infoSection: {
            backgroundColor: '#c0c0c07a',
            display: 'inline-block',
            width: '100%',
            padding: '5px',
            marginTop: '5px',
            marginBottom: '10px',
        },
    };

    const _handleClose = () =>
        dispatch(
            loanActions.setLoanSummaryModal({
                loanSummaryModal: { isOpen: false },
            })
        );

    useEffect(() => {
        async function loanIdChanged() {
            if (loanId && loanId > 0) {
                await loadLoanInfo(loanId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        loanIdChanged();
    }, [isOpen, loanId]);

    const resetState = () => {
        set_loan(null);
    };

    const loadLoanInfo = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await loanApi.getLoanSummary(id);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }
            const loan = response.result;
            set_loan(loan);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please refresh the page');
        } finally {
            set_loadingOpen(false);
        }
    };

    const getNoticeStyle = () => {
        switch (loan.noticeStatusId) {
            case NoticeStatusType.Emailed:
            case NoticeStatusType.Generated:
                return { paddingTop: '8px', color: 'green' };
            case NoticeStatusType.Pending:
                return { paddingTop: '8px', color: 'darkred' };
            case NoticeStatusType.Error:
                return { paddingTop: '8px', color: 'darkred' };
            default:
                return { paddingTop: '8px', color: 'gray' };
        }
    };

    const getNoticeTitle = () => {
        var noticeTypeName = NoticeTypeOptions.find((f) => f.id === loan.noticeTypeId)?.name;
        switch (loan.noticeStatusId) {
            case NoticeStatusType.Emailed:
                return `The notice '${noticeTypeName}' was sent at ${formatDateTime(loan.NoticeDate)}`;
            case NoticeStatusType.Generated:
                return `The notice '${noticeTypeName}' has been generated but not yet emailed`;
            case NoticeStatusType.Pending:
                return `The notice '${noticeTypeName}' to be sent at ${formatDateTime(loan.NoticeDate)} will be cancelled`;
            case NoticeStatusType.Error:
                return `The notice '${noticeTypeName}' experiences an Error`;
            default:
                return 'No Notice has been sent or scheduled';
        }
    };

    const getReasons = (reasons, title) => {
        if (!reasons || reasons.length === 0) {
            return [
                {
                    title,
                    reasons: ['No reasons recorded'],
                },
            ];
        }
        return [
            {
                title,
                reasons,
            },
        ];
    };

    const onTitleRender = (e) => {
        return <span style={{ fontSize: 'smaller' }}>{e.title}</span>;
    };

    const onReasonsRender = (e) => {
        const items = e.reasons.map((r, index) => {
            if (r != null) {
                return <li key={'reason' + index}>{r}</li>;
            } else {
                return <li key={'reason' + index}> None Specified</li>;
            }
        });
        return <ul style={{ marginBottom: '0' }}>{items}</ul>;
    };

    const formatMultipleAppraisedDates = () => {
        if (loan.allAppraisedDatesSameMonthAndYear) {
            return formatDateMonthYear(loan.allAppraisedDatesSameMonthAndYearDisplay);
        } else {
            return 'Multiple';
        }
    };

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '700px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form name='summaryForm'>
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Loan Summary</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                {loan.loanTypeId !== LoanType.MasterNote && (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={getNoticeStyle()}
                                                        >
                                                            <i title={getNoticeTitle()}>
                                                                <FontAwesomeIcon icon={faPencil} />
                                                            </i>
                                                            &nbsp;&nbsp;
                                                            {getNoticeTitle()}
                                                        </div>
                                                    </div>
                                                )}

                                                <div style={styles.infoSection}>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <h2 className='decorated'>
                                                                {getLoanTypeName(loan.loanTypeId)} Loan Information
                                                            </h2>
                                                            {loan.statusComment && (
                                                                <div
                                                                    style={{
                                                                        fontSize: '14px',
                                                                        marginBottom: '10px',
                                                                        fontStyle: 'italic',
                                                                    }}
                                                                >
                                                                    <b>{loan.statusComment}</b>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            BFI #: <b>{loan.bfiNumber}</b>
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-6'
                                                            style={styles.formColumn}
                                                        >
                                                            Loan #: <b>{loan.loanNumber}</b>
                                                        </div>
                                                    </div>
                                                    {(loan.borrowerEntity || loan.borrowerContact) && (
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                Borrower:
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-9'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    {loan.borrowerEntity && <span>{loan.borrowerEntity}</span>}
                                                                    {loan.borrowerContact && (
                                                                        <span>
                                                                            {loan.borrowerEntity && <br />}
                                                                            {loan.borrowerContact}
                                                                        </span>
                                                                    )}
                                                                </b>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(loan.brokerName || loan.brokerContact) && (
                                                        <div
                                                            className='row'
                                                            style={styles.formRow}
                                                        >
                                                            <div
                                                                className='columns small-12 large-3'
                                                                style={styles.formColumn}
                                                            >
                                                                Broker:
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-9'
                                                                style={styles.formColumn}
                                                            >
                                                                <b>
                                                                    {loan.brokerName && <span>{loan.brokerName}</span>}
                                                                    {loan.brokerContact && (
                                                                        <span>
                                                                            {loan.brokerName && <br />}
                                                                            {loan.brokerContact}
                                                                        </span>
                                                                    )}
                                                                </b>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            Underwriter:
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-9'
                                                            style={styles.formColumn}
                                                        >
                                                            <b>{loan.underwriter ? loan.underwriter : 'Not Set'}</b>
                                                        </div>
                                                    </div>
                                                    <br />
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            Note Date:
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-9'
                                                            style={styles.formColumn}
                                                        >
                                                            <b>{loan.noteDate ? formatDate(loan.noteDate) : 'Not Set'}</b>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            Orig. Maturity:
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-9'
                                                            style={styles.formColumn}
                                                        >
                                                            <b>{loan.maturityDate ? formatDate(loan.maturityDate) : 'Not Set'}</b>
                                                            &nbsp;&nbsp;&nbsp;
                                                            {getMonths(loan.noteDate, loan.maturityDate)}
                                                        </div>
                                                    </div>
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-3'
                                                            style={styles.formColumn}
                                                        >
                                                            Commitment
                                                        </div>
                                                        <div
                                                            className='columns small-12 large-9'
                                                            style={styles.formColumn}
                                                        >
                                                            <b>
                                                                {loan.commitment ? formatCurrency(loan.commitment, 0) : 'Not Set'}
                                                            </b>
                                                        </div>
                                                    </div>
                                                    {loan.extensions
                                                        ?.filter((e) => e.appraisalUpdated)
                                                        .map((e, index) => (
                                                            <div
                                                                key={'ext' + e.id}
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-3'
                                                                    style={styles.formColumn}
                                                                >
                                                                    Extension {index + 1}:
                                                                </div>
                                                                <div
                                                                    className='columns small-12 large-9'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <b>{formatDate(e.extensionDate)}</b>
                                                                    &nbsp;&nbsp;&nbsp;
                                                                    {getMonths(
                                                                        index === 0
                                                                            ? loan.maturityDate
                                                                            : loan.extensions[index - 1].extensionDate,
                                                                        e.extensionDate
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}

                                                    {loan.statusId >= LoanStatusType.Approved && (
                                                        <>
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-3'
                                                                    style={styles.formColumn}
                                                                >
                                                                    Status
                                                                </div>
                                                                <div
                                                                    className='columns small-12 large-9'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <b>
                                                                        {
                                                                            LoanStatusOptions.find((a) => a.id === loan.statusId)
                                                                                ?.name
                                                                        }
                                                                    </b>
                                                                    {loan.statusId === LoanStatusType.Lost &&
                                                                        loan.lostTypeId !== null &&
                                                                        loan.lostTypeId !== undefined && (
                                                                            <b>
                                                                                {' - '}
                                                                                {
                                                                                    LostLoanTypeOptions.find(
                                                                                        (o) => o.id === loan.lostTypeId
                                                                                    )?.name
                                                                                }
                                                                            </b>
                                                                        )}
                                                                </div>
                                                            </div>
                                                            {loan.lostReasons !== null &&
                                                                loan.lostReasons !== undefined &&
                                                                loan.lostReasons.length > 0 && (
                                                                    <div
                                                                        className='row'
                                                                        style={styles.formRow}
                                                                    >
                                                                        <div
                                                                            className='columns small-12 large-12'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            <Accordion
                                                                                dataSource={getReasons(
                                                                                    loan.lostReasons,
                                                                                    'Reasons for Loss'
                                                                                )}
                                                                                collapsible={false}
                                                                                itemTitleRender={onTitleRender}
                                                                                itemRender={onReasonsRender}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                        </>
                                                    )}
                                                    {loan.loanTypeId !== LoanType.MasterNote && (
                                                        <>
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div className='columns small-12 large-12'>
                                                                    <h2 className='decorated'>Properties</h2>
                                                                    <ul>
                                                                        {loan.properties.map((p) => {
                                                                            return (
                                                                                <li key={'p' + p.id}>
                                                                                    {p.street1} {p.street2}, {p.city} {p.state},{' '}
                                                                                    {p.zipCode}
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-12'
                                                                    style={styles.formColumn}
                                                                >
                                                                    <h2 className='decorated'>Appraisals</h2>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                <div
                                                                    className='columns small-12 large-4'
                                                                    style={styles.formColumn}
                                                                >
                                                                    Value:{' '}
                                                                    <b>
                                                                        {loan.appraisedValue
                                                                            ? formatCurrency(loan.appraisedValue, 0)
                                                                            : 'Not Set'}
                                                                    </b>
                                                                </div>
                                                                <div
                                                                    className='columns small-12 large-4'
                                                                    style={styles.formColumn}
                                                                >
                                                                    Date:{' '}
                                                                    <b>
                                                                        {loan.appraisedDate
                                                                            ? formatDate(loan.appraisedDate)
                                                                            : loan.hasMultipleAppraisedDates
                                                                              ? formatMultipleAppraisedDates()
                                                                              : 'Not Set'}
                                                                    </b>
                                                                </div>
                                                                <div
                                                                    className='columns small-12 large-3'
                                                                    style={styles.formColumn}
                                                                >
                                                                    LTV: <b>{loan.ltv && loan.ltv + '%'}</b>
                                                                </div>
                                                            </div>
                                                            {loan.extensions
                                                                ?.filter((e) => e.appraisalUpdated)
                                                                .map((e) => (
                                                                    <div
                                                                        key={'ext' + e.id}
                                                                        className='row'
                                                                        style={styles.formRow}
                                                                    >
                                                                        <div
                                                                            className='columns small-12 large-4'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            Value: <b>{formatCurrency(e.appraisedValue, 0)}</b>
                                                                        </div>
                                                                        <div
                                                                            className='columns small-12 large-4'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            Date:{' '}
                                                                            <b>
                                                                                {e.appraisedDate && formatDate(e.appraisedDate)}
                                                                            </b>
                                                                        </div>
                                                                        <div
                                                                            className='columns small-12 large-3'
                                                                            style={styles.formColumn}
                                                                        >
                                                                            LTV: <b>{e.ltv && e.ltv + '%'}</b>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default LoanSummaryModal;
